import React, { useState } from 'react';

const ControlPanel = ({ 
  webCount, 
  webDensity, 
  complexity,
  onDensityChange, 
  onComplexityChange,
  isLoading,
  spiderCount,
  onSpiderCountChange,
  sizeVariation,
  onSizeVariationChange,
  onExportImage,
  viewMode = 'spider2',
  onViewModeChange,
  model3DSettings,
  onModel3DSettingsChange
}) => {
  
  const [isMinimized, setIsMinimized] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [activeTab, setActiveTab] = useState('controls');
  const [exportSettings, setExportSettings] = useState({
    format: 'png',
    quality: 0.95
  });
  
  // Estados para configuración 3D
  const [rotation3D, setRotation3D] = useState(model3DSettings?.autoRotate ?? true);
  const [wireframe3D, setWireframe3D] = useState(model3DSettings?.wireframe ?? false);
  const [lightIntensity, setLightIntensity] = useState(model3DSettings?.lightIntensity ?? 1);
  
  // Convertir complejidad a número para el slider
  const complexityToNumber = (comp) => {
    switch(comp) {
      case 'simple': return 1;
      case 'medium': return 2;
      case 'complex': return 3;
      default: return 2;
    }
  };
  
  const numberToComplexity = (num) => {
    switch(num) {
      case 1: return 'simple';
      case 2: return 'medium';
      case 3: return 'complex';
      default: return 'medium';
    }
  };
  
  // Estilos base
  const styles = {
    panelWrapper: {
      position: 'fixed',
      bottom: '20px',
      left: '20px',
      zIndex: 100,
    },
    minimizedButton: {
      width: '56px',
      height: '56px',
      borderRadius: '14px',
      background: 'rgba(10, 10, 10, 0.92)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(200, 180, 150, 0.25)',
      color: 'rgba(200, 180, 150, 0.9)',
      fontSize: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      backdropFilter: 'blur(12px)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: 'inherit',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
    },
    panel: {
      width: '440px',
      padding: '0',
      background: 'rgba(10, 10, 10, 0.95)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(200, 180, 150, 0.2)',
      borderRadius: '12px',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.6)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      overflow: 'hidden',
      position: 'relative',
    },
    minimizeButton: {
      position: 'absolute',
      top: '50%',
      right: '12px',
      transform: 'translateY(-50%)',
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      background: 'rgba(200, 180, 150, 0.08)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(200, 180, 150, 0.15)',
      color: 'rgba(200, 180, 150, 0.5)',
      fontSize: '16px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
      zIndex: 10,
      lineHeight: 1,
    },
    tabBar: {
      display: 'flex',
      borderBottom: '1px solid rgba(200, 180, 150, 0.15)',
      background: 'rgba(5, 5, 5, 0.5)',
      position: 'relative',
      paddingRight: '40px',
    },
    tab: {
      flex: 1,
      minWidth: 0,
      padding: '10px 4px',
      whiteSpace: 'nowrap',
      background: 'transparent',
      border: 'none',
      borderBottom: '2px solid transparent',
      color: 'rgba(200, 180, 150, 0.5)',
      fontSize: '9px',
      fontWeight: 500,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
    },
    tabActive: {
      borderBottom: '2px solid rgba(200, 180, 150, 0.7)',
      color: 'rgba(200, 180, 150, 0.9)',
      background: 'rgba(200, 180, 150, 0.05)',
    },
    tabContent: {
      padding: '15px',
      maxHeight: '400px',
      overflowY: 'auto',
    },
    header: {
      marginBottom: '10px',
      paddingBottom: '8px',
      borderBottom: '1px solid rgba(200, 180, 150, 0.15)',
    },
    title: {
      fontSize: '13px',
      fontWeight: 300,
      letterSpacing: '0.15em',
      margin: '0 0 2px 0',
      color: 'rgba(200, 180, 150, 0.9)',
    },
    subtitle: {
      fontSize: '9px',
      color: 'rgba(200, 180, 150, 0.5)',
      letterSpacing: '0.1em',
    },
    viewModeSection: {
      marginBottom: '15px',
      padding: '8px',
      background: 'rgba(200, 180, 150, 0.02)',
      borderRadius: '4px',
      border: '1px solid rgba(200, 180, 150, 0.08)',
    },
    viewModeTitle: {
      fontSize: '9px',
      color: 'rgba(200, 180, 150, 0.6)',
      marginBottom: '10px',
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      fontWeight: 300,
    },
    viewModeButtons: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '6px',
    },
    viewModeBtn: {
      padding: '8px 6px',
      background: 'rgba(15, 15, 15, 0.6)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(200, 180, 150, 0.1)',
      borderRadius: '3px',
      color: 'rgba(200, 180, 150, 0.4)',
      fontSize: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: 'inherit',
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontWeight: 500,
      position: 'relative',
      overflow: 'hidden',
    },
    statsSection: {
      marginBottom: '15px',
      padding: '8px',
      background: 'rgba(200, 180, 150, 0.03)',
      borderRadius: '4px',
    },
    statsRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '4px',
      fontSize: '9px',
    },
    statsLabel: {
      color: 'rgba(200, 180, 150, 0.5)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    statsValue: {
      color: 'rgba(200, 180, 150, 0.8)',
      fontWeight: 500,
    },
    settingsSection: {
      paddingTop: '8px',
      borderTop: '1px solid rgba(200, 180, 150, 0.1)',
    },
    sliderRow: {
      marginBottom: '12px',
    },
    sliderLabel: {
      fontSize: '9px',
      color: 'rgba(200, 180, 150, 0.5)',
      marginBottom: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    sliderValue: {
      fontSize: '10px',
      color: 'rgba(200, 180, 150, 0.7)',
      fontWeight: 500,
    },
    slider: {
      width: '100%',
      height: '3px',
      background: 'rgba(200, 180, 150, 0.1)',
      outline: 'none',
      borderRadius: '2px',
      WebkitAppearance: 'none',
      appearance: 'none',
      cursor: 'pointer',
    },
    helpIcon: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      background: 'rgba(200, 180, 150, 0.2)',
      color: 'rgba(200, 180, 150, 0.6)',
      fontSize: '8px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'help',
      marginLeft: '4px',
    },
    mathSection: {
      fontSize: '10px',
      color: 'rgba(200, 180, 150, 0.7)',
      lineHeight: 1.6,
    },
    mathBlock: {
      marginBottom: '15px',
      padding: '10px',
      background: 'rgba(200, 180, 150, 0.05)',
      borderRadius: '4px',
      border: '1px solid rgba(200, 180, 150, 0.1)',
    },
    mathTitle: {
      fontSize: '11px',
      fontWeight: 600,
      color: 'rgba(200, 180, 150, 0.9)',
      marginBottom: '5px',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    mathFormula: {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: 'rgba(200, 180, 150, 0.8)',
      background: 'rgba(0, 0, 0, 0.3)',
      padding: '5px',
      borderRadius: '2px',
      marginTop: '5px',
      display: 'block',
    },
    mathDescription: {
      fontSize: '9px',
      color: 'rgba(200, 180, 150, 0.6)',
      marginTop: '8px',
      lineHeight: 1.5,
    },
    exportSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
    },
    exportOption: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px',
      background: 'rgba(200, 180, 150, 0.05)',
      borderRadius: '4px',
      border: '1px solid rgba(200, 180, 150, 0.1)',
    },
    exportLabel: {
      fontSize: '10px',
      color: 'rgba(200, 180, 150, 0.7)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    exportButtons: {
      display: 'flex',
      gap: '5px',
    },
    exportBtn: {
      padding: '5px 10px',
      background: 'rgba(20, 20, 20, 0.8)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(200, 180, 150, 0.2)',
      borderRadius: '3px',
      color: 'rgba(200, 180, 150, 0.6)',
      fontSize: '9px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
    },
    exportBtnActive: {
      background: 'rgba(200, 180, 150, 0.15)',
      borderColor: 'rgba(200, 180, 150, 0.3)',
      color: 'rgba(200, 180, 150, 0.9)',
    },
    exportMainBtn: {
      width: '100%',
      padding: '12px',
      background: 'linear-gradient(135deg, rgba(200, 180, 150, 0.2) 0%, rgba(200, 180, 150, 0.1) 100%)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(200, 180, 150, 0.3)',
      borderRadius: '6px',
      color: 'rgba(200, 180, 150, 0.9)',
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
    },
    complexityIndicator: {
      marginTop: '4px',
      fontSize: '9px',
      color: 'rgba(200, 180, 150, 0.6)',
      textAlign: 'center',
    },
    typeHeader: {
      padding: '8px',
      background: 'rgba(200, 180, 150, 0.08)',
      borderRadius: '4px',
      marginBottom: '10px',
      borderLeft: '3px solid rgba(200, 180, 150, 0.3)',
    },
    comparisonTable: {
      width: '100%',
      marginTop: '10px',
      borderCollapse: 'collapse',
    },
    comparisonRow: {
      borderBottom: '1px solid rgba(200, 180, 150, 0.1)',
    },
    comparisonCell: {
      padding: '6px 4px',
      fontSize: '9px',
    },
    conceptBox: {
      background: 'rgba(0, 0, 0, 0.2)',
      padding: '8px',
      borderRadius: '4px',
      marginTop: '8px',
      fontSize: '9px',
      borderLeft: '2px solid rgba(200, 180, 150, 0.2)',
    },
    // Nuevos estilos para 3D
    toggle3D: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px',
      background: 'rgba(200, 180, 150, 0.05)',
      borderRadius: '4px',
      border: '1px solid rgba(200, 180, 150, 0.1)',
      marginBottom: '10px',
    },
    toggleSwitch: {
      width: '40px',
      height: '20px',
      background: 'rgba(200, 180, 150, 0.1)',
      borderRadius: '10px',
      position: 'relative',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    toggleSwitchActive: {
      background: 'rgba(200, 180, 150, 0.3)',
    },
    toggleKnob: {
      width: '16px',
      height: '16px',
      background: 'rgba(200, 180, 150, 0.5)',
      borderRadius: '50%',
      position: 'absolute',
      top: '2px',
      left: '2px',
      transition: 'all 0.3s ease',
    },
    toggleKnobActive: {
      left: '22px',
      background: 'rgba(200, 180, 150, 0.9)',
    },
    model3DBtn: {
      width: '100%',
      padding: '12px',
      background: 'rgba(200, 180, 150, 0.08)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(200, 180, 150, 0.2)',
      borderRadius: '6px',
      color: 'rgba(200, 180, 150, 0.7)',
      fontSize: '11px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
      marginBottom: '10px',
    }
  };
  
  const getButtonStyle = (base, buttonId, isActive = false) => {
    let style = { ...base };
    
    if (isActive) {
      style.background = 'rgba(200, 180, 150, 0.12)';
      style.borderColor = 'rgba(200, 180, 150, 0.25)';
      style.color = 'rgba(200, 180, 150, 0.95)';
      style.fontWeight = 600;
      style.boxShadow = 'inset 0 0 10px rgba(200, 180, 150, 0.05)';
    }
    
    if (hoveredButton === buttonId && !isLoading && !isActive) {
      style.background = 'rgba(200, 180, 150, 0.06)';
      style.borderColor = 'rgba(200, 180, 150, 0.15)';
      style.color = 'rgba(200, 180, 150, 0.7)';
      style.transform = 'translateY(-1px)';
    }
    
    if (isLoading) {
      style.opacity = 0.3;
      style.cursor = 'not-allowed';
    }
    
    return style;
  };
  
  const handleExport = () => {
    if (onExportImage) {
      onExportImage(exportSettings.format, exportSettings.quality);
    }
  };
  
  const handle3DSettingChange = (setting, value) => {
    if (onModel3DSettingsChange) {
      onModel3DSettingsChange({ [setting]: value });
    }
    
    switch(setting) {
      case 'autoRotate':
        setRotation3D(value);
        break;
      case 'wireframe':
        setWireframe3D(value);
        break;
      case 'lightIntensity':
        setLightIntensity(value);
        break;
    }
  };
  
  const sliderStyle = `
    input[type="range"]::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 12px;
      height: 12px;
      background: rgba(200, 180, 150, 0.8);
      cursor: pointer;
      border-radius: 50%;
      border: 2px solid rgba(10, 10, 10, 0.9);
    }
    input[type="range"]::-moz-range-thumb {
      width: 12px;
      height: 12px;
      background: rgba(200, 180, 150, 0.8);
      cursor: pointer;
      border-radius: 50%;
      border: 2px solid rgba(10, 10, 10, 0.9);
    }
  `;
  
  // Calcular estadísticas estimadas
  const getComplexityStats = () => {
    switch(complexity) {
      case 'simple':
        return { radials: '8-16', rings: '4-8', spiral: 'No' };
      case 'medium':
        return { radials: '16-24', rings: '8-12', spiral: 'Simple' };
      case 'complex':
        return { radials: '24-36', rings: '12-20', spiral: 'Compleja' };
      default:
        return { radials: '16-24', rings: '8-12', spiral: 'Simple' };
    }
  };
  
  const stats = getComplexityStats();
  
  // Si está minimizado, mostrar solo el botón
  if (isMinimized) {
    return (
      <div style={styles.panelWrapper}>
        <button
          style={{
            ...styles.minimizedButton,
            ...(hoveredButton === 'minimized' ? {
              background: 'rgba(200, 180, 150, 0.12)',
              borderColor: 'rgba(200, 180, 150, 0.4)',
              transform: 'scale(1.08)',
              boxShadow: '0 6px 20px rgba(200, 180, 150, 0.25)',
            } : {})
          }}
          onMouseEnter={() => setHoveredButton('minimized')}
          onMouseLeave={() => setHoveredButton(null)}
          onClick={() => setIsMinimized(false)}
          title="Abrir panel de control"
        >
          <div style={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            letterSpacing: '0.05em',
            fontWeight: 600,
          }}>
            M3
          </div>
        </button>
      </div>
    );
  }
  
  // Panel completo
  return (
    <div style={styles.panelWrapper}>
      <style>{sliderStyle}</style>
      
      <div style={styles.panel}>
        <div style={styles.tabBar}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'controls' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('controls')}
          >
            Controles
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'math' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('math')}
          >
            Matemática
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === '3d' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('3d')}
          >
            3D
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'export' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('export')}
          >
            Exportar
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'spider' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('spider')}
          >
            Spider
          </button>
          
          {/* Botón de minimizar en el tab bar */}
          <button
            style={{
              ...styles.minimizeButton,
              ...(hoveredButton === 'minimize' ? {
                background: 'rgba(200, 180, 150, 0.15)',
                borderColor: 'rgba(200, 180, 150, 0.3)',
                color: 'rgba(200, 180, 150, 0.8)',
              } : {})
            }}
            onMouseEnter={() => setHoveredButton('minimize')}
            onMouseLeave={() => setHoveredButton(null)}
            onClick={() => setIsMinimized(true)}
            title="Minimizar panel"
          >
            −
          </button>
        </div>
        
        <div style={styles.tabContent}>
          {/* TAB: SPIDERTRACK */}
          {activeTab === 'spider' && (
            <>
              <div style={styles.header}>
                <h1 style={styles.title}>SpiderTrack</h1>
                <p style={styles.mathDescription}>
                  El movimiento de las patas al fijar el hilo se convierte en dibujo y en sonido.
                  Coge un vídeo o la webcam, aísla la araña por umbral, sigue las puntas de las
                  patas y va acumulando cada fotograma hasta formar la tela.
                </p>
              </div>
              <button
                style={{
                  ...styles.exportMainBtn,
                  width: '100%',
                }}
                onClick={() => onViewModeChange && onViewModeChange('spidertrack')}
              >
                Abrir a pantalla completa
              </button>
            </>
          )}

          {/* TAB: CONTROLES */}
          {activeTab === 'controls' && (
            <>
              <div style={styles.header}>
                <h1 style={styles.title}>MEMBRANA Vol. 3</h1>
                <div style={styles.subtitle}>Las Hilanderas · Diego Velázquez</div>
              </div>
              
              {/* MODOS DE VISUALIZACIÓN */}
              <div style={styles.viewModeSection}>
                <div style={styles.viewModeTitle}>Modo de Visualización</div>
                <div style={styles.viewModeButtons}>
                  <button
                    style={getButtonStyle(styles.viewModeBtn, 'view-painting', viewMode === 'painting')}
                    onMouseEnter={() => setHoveredButton('view-painting')}
                    onMouseLeave={() => setHoveredButton(null)}
                    onClick={() => onViewModeChange('painting')}
                    disabled={isLoading}
                  >
                    Pintura
                  </button>
                  <button
                    style={getButtonStyle(styles.viewModeBtn, 'view-spider1', viewMode === 'spider1')}
                    onMouseEnter={() => setHoveredButton('view-spider1')}
                    onMouseLeave={() => setHoveredButton(null)}
                    onClick={() => onViewModeChange('spider1')}
                    disabled={isLoading}
                  >
                    Araña I
                  </button>
                  <button
                    style={getButtonStyle(styles.viewModeBtn, 'view-spider2', viewMode === 'spider2')}
                    onMouseEnter={() => setHoveredButton('view-spider2')}
                    onMouseLeave={() => setHoveredButton(null)}
                    onClick={() => onViewModeChange('spider2')}
                    disabled={isLoading}
                  >
                    Araña II
                  </button>
                  <button
                    style={getButtonStyle(styles.viewModeBtn, 'view-gallery', viewMode === 'gallery')}
                    onMouseEnter={() => setHoveredButton('view-gallery')}
                    onMouseLeave={() => setHoveredButton(null)}
                    onClick={() => onViewModeChange('gallery')}
                    disabled={isLoading}
                  >
                    Galería
                  </button>
                  <button
                    style={getButtonStyle(styles.viewModeBtn, 'view-3d', viewMode === 'model3d')}
                    onMouseEnter={() => setHoveredButton('view-3d')}
                    onMouseLeave={() => setHoveredButton(null)}
                    onClick={() => onViewModeChange('model3d')}
                    disabled={isLoading}
                  >
                    Modelo 3D
                  </button>
                </div>
              </div>
              
              {/* ESTADÍSTICAS - Solo para modos con telarañas */}
              {viewMode !== 'painting' && viewMode !== 'gallery' && viewMode !== 'model3d' && (
                <div style={styles.statsSection}>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>Telarañas generadas</span>
                    <span style={styles.statsValue}>{webCount}</span>
                  </div>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>Radios por telaraña</span>
                    <span style={styles.statsValue}>{stats.radials}</span>
                  </div>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>Anillos concéntricos</span>
                    <span style={styles.statsValue}>{stats.rings}</span>
                  </div>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>Espiral de captura</span>
                    <span style={styles.statsValue}>{stats.spiral}</span>
                  </div>
                </div>
              )}
              
              {/* CONFIGURACIÓN - Solo para modos con telarañas */}
              {viewMode !== 'painting' && viewMode !== 'gallery' && viewMode !== 'model3d' && (
                <div style={styles.settingsSection}>
                  {/* Densidad */}
                  <div style={styles.sliderRow}>
                    <div style={styles.sliderLabel}>
                      <span>
                        Densidad de telarañas ρ
                        <span 
                          style={styles.helpIcon}
                          onMouseEnter={() => setShowTooltip('density')}
                          onMouseLeave={() => setShowTooltip(null)}
                        >
                          ?
                        </span>
                      </span>
                      <span style={styles.sliderValue}>{webDensity}</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="3000"
                      step="10"
                      value={webDensity}
                      onChange={(e) => onDensityChange(parseInt(e.target.value))}
                      style={styles.slider}
                      disabled={isLoading}
                    />
                  </div>
                  
                  {/* Complejidad como slider */}
                  <div style={styles.sliderRow}>
                    <div style={styles.sliderLabel}>
                      <span>
                        Complejidad estructural Ω
                        <span 
                          style={styles.helpIcon}
                          onMouseEnter={() => setShowTooltip('complexity')}
                          onMouseLeave={() => setShowTooltip(null)}
                        >
                          ?
                        </span>
                      </span>
                      <span style={styles.sliderValue}>
                        {complexity === 'simple' ? 'Simple' : 
                         complexity === 'medium' ? 'Media' : 'Compleja'}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="1"
                      value={complexityToNumber(complexity)}
                      onChange={(e) => onComplexityChange(numberToComplexity(parseInt(e.target.value)))}
                      style={styles.slider}
                      disabled={isLoading}
                    />
                    <div style={styles.complexityIndicator}>
                      {complexity === 'simple' && 'Estructura minimalista con geometría básica'}
                      {complexity === 'medium' && 'Equilibrio entre detalle y rendimiento'}
                      {complexity === 'complex' && 'Máxima fidelidad biológica'}
                    </div>
                  </div>
                  
                  {/* Variación de tamaño - Solo tipo 1 */}
                  {viewMode === 'spider1' && (
                    <div style={styles.sliderRow}>
                      <div style={styles.sliderLabel}>
                        <span>
                          Variación estocástica σ
                          <span 
                            style={styles.helpIcon}
                            onMouseEnter={() => setShowTooltip('variation')}
                            onMouseLeave={() => setShowTooltip(null)}
                          >
                            ?
                          </span>
                        </span>
                        <span style={styles.sliderValue}>{Math.round(sizeVariation * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={sizeVariation * 100}
                        onChange={(e) => onSizeVariationChange(parseInt(e.target.value) / 100)}
                        style={styles.slider}
                        disabled={isLoading}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          
          {/* TAB: MATEMÁTICA REORGANIZADA */}
          {activeTab === 'math' && (
            <div style={styles.mathSection}>
              <div style={styles.header}>
                <h1 style={styles.title}>Morfogénesis Computacional</h1>
                <div style={styles.subtitle}>De Píxeles a Geometría Arácnida</div>
              </div>
              
              {/* INTRODUCCIÓN */}
              <div style={styles.mathBlock}>
                <div style={styles.mathTitle}>Introducción</div>
                <div style={styles.mathDescription}>
                  Este proyecto traduce la composición visual de "Las Hilanderas" de Velázquez 
                  a geometrías de telarañas mediante algoritmos bioinspirados. 
                  Se basan en investigaciones sobre construcción de telarañas 
                  [Corver et al., 2021], geometría orbital [Regassa et al., 2021] 
                  y modelado 3D [Su et al., 2018].
                </div>
              </div>
              
              {/* CONCEPTOS FUNDAMENTALES */}
              <div style={styles.mathBlock}>
                <div style={styles.mathTitle}>Conceptos Pictóricos Fundamentales</div>
                
                <div style={styles.conceptBox}>
                  <strong>Luminancia (L)</strong>: Brillo percibido de un píxel<br/>
                  <code style={{...styles.mathFormula, marginTop: '4px'}}>L = 0.299R + 0.587G + 0.114B</code>
                  Estándar ITU-R BT.601 para visión humana. Los coeficientes reflejan 
                  la sensibilidad del ojo: verde (58.7%) "{'>'}" rojo (29.9%) "{'>'}" azul (11.4%)
                </div>
                
                <div style={styles.conceptBox}>
                  <strong>Gradiente (∇I)</strong>: Cambio de intensidad en x,y<br/>
                  <code style={{...styles.mathFormula, marginTop: '4px'}}>∇I = (∂I/∂x, ∂I/∂y)</code>
                  Calculado mediante operador Sobel: detecta bordes y contornos 
                  comparando píxeles vecinos con kernels de convolución 3x3
                </div>
                
                <div style={styles.conceptBox}>
                  <strong>Contraste (σ)</strong>: Magnitud del cambio local<br/>
                  <code style={{...styles.mathFormula, marginTop: '4px'}}>σ = ||∇I|| = √(∂I/∂x)² + (∂I/∂y)²</code>
                  Norma euclidiana del gradiente. Mayor contraste = bordes más definidos
                </div>
                
                <div style={styles.conceptBox}>
                  <strong>Profundidad (Z)</strong>: Coordenada espacial derivada<br/>
                  <code style={{...styles.mathFormula, marginTop: '4px'}}>Z = (L - 0.5) × 30</code>
                  Mapea luminancia a profundidad: oscuro = cerca, claro = lejos
                </div>
              </div>
              
              {/* GEOMETRÍAS DE TELARAÑA */}
              <div style={styles.mathBlock}>
                <div style={styles.mathTitle}>Geometrías Biológicas</div>
                
                <div style={styles.conceptBox}>
                  <strong>Espiral de Arquímedes</strong>: Estructura de captura<br/>
                  <code style={{...styles.mathFormula, marginTop: '4px'}}>r = a + b·θ</code>
                  • Radio crece linealmente con el ángulo<br/>
                  • Espaciado uniforme entre vueltas<br/>
                  • Observada en telarañas reales [Regassa et al., 2021]
                </div>
                
                <div style={styles.conceptBox}>
                  <strong>Catenaria</strong>: Curva de equilibrio gravitacional<br/>
                  <code style={{...styles.mathFormula, marginTop: '4px'}}>y = a·cosh(x/a)</code>
                  • Forma que adopta un hilo suspendido<br/>
                  • Minimiza energía potencial<br/>
                  • Parámetro 'a' = longitud/(2×tensión)
                </div>
              </div>
            </div>
          )}
          
          {/* TAB: 3D */}
          {activeTab === '3d' && (
            <div style={styles.mathSection}>
              <div style={styles.header}>
                <h1 style={styles.title}>Visualización 3D</h1>
                <div style={styles.subtitle}>Modelo Tridimensional</div>
              </div>
              
              {/* Información del modelo */}
              <div style={styles.mathBlock}>
                <div style={styles.mathTitle}>Modelo Cargado</div>
                <div style={styles.mathDescription}>
                  Archivo: {model3DSettings?.modelPath?.split('/').pop() || 'model.ply'}<br/>
                  Formato: PLY / GLTF / GLB<br/>
                  <br/>
                  <small style={{ opacity: 0.7 }}>
                    Para cambiar el modelo, edite la ruta en model3DSettings.modelPath
                  </small>
                </div>
              </div>
              
              {/* Controles de visualización */}
              <div style={styles.mathBlock}>
                <div style={styles.mathTitle}>Configuración de Vista</div>
                
                {/* Auto-rotación */}
                <div style={styles.toggle3D}>
                  <span style={styles.exportLabel}>Auto-rotación</span>
                  <div 
                    style={{
                      ...styles.toggleSwitch,
                      ...(rotation3D ? styles.toggleSwitchActive : {})
                    }}
                    onClick={() => handle3DSettingChange('autoRotate', !rotation3D)}
                  >
                    <div 
                      style={{
                        ...styles.toggleKnob,
                        ...(rotation3D ? styles.toggleKnobActive : {})
                      }}
                    />
                  </div>
                </div>
                
                {/* Modo Wireframe */}
                <div style={styles.toggle3D}>
                  <span style={styles.exportLabel}>Modo Wireframe</span>
                  <div 
                    style={{
                      ...styles.toggleSwitch,
                      ...(wireframe3D ? styles.toggleSwitchActive : {})
                    }}
                    onClick={() => handle3DSettingChange('wireframe', !wireframe3D)}
                  >
                    <div 
                      style={{
                        ...styles.toggleKnob,
                        ...(wireframe3D ? styles.toggleKnobActive : {})
                      }}
                    />
                  </div>
                </div>
                
                {/* Intensidad de luz */}
                <div style={styles.sliderRow}>
                  <div style={styles.sliderLabel}>
                    <span>Intensidad de Luz</span>
                    <span style={styles.sliderValue}>{Math.round(lightIntensity * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={lightIntensity * 100}
                    onChange={(e) => handle3DSettingChange('lightIntensity', parseInt(e.target.value) / 100)}
                    style={styles.slider}
                  />
                </div>
              </div>
              
              {/* Instrucciones de uso */}
              <div style={styles.mathBlock}>
                <div style={styles.mathTitle}>Controles del Visor</div>
                <div style={styles.mathDescription}>
                  <strong>Mouse:</strong><br/>
                  • Clic izquierdo + arrastrar: Rotar vista<br/>
                  • Scroll: Acercar / Alejar<br/>
                  • Clic derecho + arrastrar: Desplazar<br/>
                  <br/>
                  <strong>Teclado:</strong><br/>
                  • R: Resetear vista<br/>
                  • ESC: Volver al modo anterior<br/>
                </div>
              </div>
              
              {/* Botón para cargar modelo */}
              <button
                style={{
                  ...styles.model3DBtn,
                  ...(hoveredButton === 'load-model' ? {
                    background: 'rgba(200, 180, 150, 0.15)',
                    borderColor: 'rgba(200, 180, 150, 0.3)',
                    color: 'rgba(200, 180, 150, 0.9)',
                  } : {})
                }}
                onMouseEnter={() => setHoveredButton('load-model')}
                onMouseLeave={() => setHoveredButton(null)}
                onClick={() => onViewModeChange('model3d')}
              >
                Ver Modelo 3D en Pantalla Completa
              </button>
              
              {/* Información técnica */}
              <div style={styles.conceptBox}>
                <strong>Información Técnica:</strong><br/>
                • Renderizado con Three.js / WebGL<br/>
                • Soporte para PLY, GLTF, GLB<br/>
                • Iluminación HDRI simulada<br/>
                • Sombras en tiempo real<br/>
                • Anti-aliasing activado<br/>
              </div>
            </div>
          )}
          
          {/* TAB: EXPORTAR */}
          {activeTab === 'export' && (
            <div style={styles.exportSection}>
              <div style={styles.header}>
                <h1 style={styles.title}>Exportar Imagen</h1>
                <div style={styles.subtitle}>Guardar composición actual</div>
              </div>
              
              <div style={styles.exportOption}>
                <span style={styles.exportLabel}>Formato</span>
                <div style={styles.exportButtons}>
                  {['png', 'jpeg', 'webp'].map(format => (
                    <button
                      key={format}
                      style={{
                        ...styles.exportBtn,
                        ...(exportSettings.format === format ? styles.exportBtnActive : {})
                      }}
                      onClick={() => setExportSettings({...exportSettings, format})}
                    >
                      {format.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              
              <div style={styles.exportOption}>
                <span style={styles.exportLabel}>Calidad</span>
                <div style={styles.exportButtons}>
                  {[
                    { label: 'Media', value: 0.85 },
                    { label: 'Alta', value: 0.95 },
                    { label: 'Máxima', value: 1.0 }
                  ].map(quality => (
                    <button
                      key={quality.value}
                      style={{
                        ...styles.exportBtn,
                        ...(exportSettings.quality === quality.value ? styles.exportBtnActive : {})
                      }}
                      onClick={() => setExportSettings({...exportSettings, quality: quality.value})}
                    >
                      {quality.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <button
                style={{
                  ...styles.exportMainBtn,
                  ...(hoveredButton === 'export' ? {
                    background: 'linear-gradient(135deg, rgba(200, 180, 150, 0.3) 0%, rgba(200, 180, 150, 0.2) 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(200, 180, 150, 0.2)',
                  } : {})
                }}
                onMouseEnter={() => setHoveredButton('export')}
                onMouseLeave={() => setHoveredButton(null)}
                onClick={handleExport}
                disabled={isLoading}
              >
                Descargar Imagen
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* TOOLTIPS MEJORADOS Y DETALLADOS */}
      {showTooltip === 'density' && (
        <div style={{
          position: 'absolute',
          left: '400px',
          bottom: '20px',
          background: 'rgba(30, 30, 30, 0.95)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'rgba(200, 180, 150, 0.3)',
          borderRadius: '6px',
          padding: '12px',
          fontSize: '10px',
          color: 'rgba(200, 180, 150, 0.9)',
          width: '280px',
          zIndex: 1001,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          lineHeight: 1.5,
        }}>
          <strong style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>
            Densidad de Material (ρ)
          </strong>
          Controla el número total de telarañas en la composición.
          <br/><br/>
          <strong>Función:</strong> ρ = f(1 - L)
          <br/>Donde L es la luminancia del píxel analizado.
          <br/><br/>
          <strong>Rangos recomendados:</strong>
          <br/>• 1-100: Arte minimalista, pocas telarañas aisladas
          <br/>• 100-500: Composición ligera y elegante
          <br/>• 500-1000: Densidad equilibrada (recomendado)
          <br/>• 1000-2000: Tejido denso y complejo
          <br/>• 2000-3000: Saturación máxima, efecto niebla
          <br/><br/>
          <em>Nota: Valores altos pueden afectar el rendimiento</em>
        </div>
      )}
      
      {showTooltip === 'complexity' && (
        <div style={{
          position: 'absolute',
          left: '400px',
          bottom: '20px',
          background: 'rgba(30, 30, 30, 0.95)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'rgba(200, 180, 150, 0.3)',
          borderRadius: '6px',
          padding: '12px',
          fontSize: '10px',
          color: 'rgba(200, 180, 150, 0.9)',
          width: '280px',
          zIndex: 1001,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          lineHeight: 1.5,
        }}>
          <strong style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>
            Complejidad Estructural (Ω)
          </strong>
          Define el nivel de detalle geométrico de cada telaraña individual.
          <br/><br/>
          <strong>Simple (Ω = 1):</strong>
          <br/>• 8-16 radios principales
          <br/>• 4-8 anillos concéntricos
          <br/>• Sin espiral de captura
          <br/>• ~150 vértices por telaraña
          <br/><br/>
          <strong>Media (Ω = 2):</strong>
          <br/>• 16-24 radios principales
          <br/>• 8-12 anillos concéntricos  
          <br/>• Espiral simple de 3-5 vueltas
          <br/>• ~400 vértices por telaraña
          <br/><br/>
          <strong>Compleja (Ω = 3):</strong>
          <br/>• 24-36 radios principales
          <br/>• 12-20 anillos concéntricos
          <br/>• Espiral compleja de 5-7 vueltas
          <br/>• ~800 vértices por telaraña
          <br/><br/>
          <em>Mayor complejidad = mayor fidelidad biológica</em>
        </div>
      )}
      
      {showTooltip === 'variation' && (
        <div style={{
          position: 'absolute',
          left: '400px',
          bottom: '20px',
          background: 'rgba(30, 30, 30, 0.95)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'rgba(200, 180, 150, 0.3)',
          borderRadius: '6px',
          padding: '12px',
          fontSize: '10px',
          color: 'rgba(200, 180, 150, 0.9)',
          width: '280px',
          zIndex: 1001,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          lineHeight: 1.5,
        }}>
          <strong style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>
            Variación Estocástica (σ) - Exclusiva Tipo 1
          </strong>
          Introduce aleatoriedad controlada en las dimensiones de las telarañas.
          <br/><br/>
          <strong>Fórmula:</strong> S' = S × (1 + σ × ξ)
          <br/>Donde ξ ~ U(-1, 1) es ruido uniforme
          <br/><br/>
          <strong>Efectos del valor:</strong>
          <br/>• 0%: Todas las telarañas del mismo tamaño base
          <br/>• 25%: Variación sutil, natural
          <br/>• 50%: Variación moderada (recomendado)
          <br/>• 75%: Alta variabilidad, más orgánico
          <br/>• 100%: Máxima aleatoriedad, caótico
          <br/><br/>
          Esta característica es lo que diferencia al Tipo 1 del Tipo 2,
          añadiendo un carácter artístico y generativo.
          <br/><br/>
          <em>NO disponible en Tipo 2 para mantener determinismo científico</em>
        </div>
      )}
    </div>
  );
};

export default ControlPanel;